(() => {
  'use strict';

  const ELEMENT_NAME = 'deeb-product-media-modal';
  const OPEN_ATTRIBUTE = 'open';
  const ACTIVE_CLASS = 'is-active';
  const BODY_LOCK_CLASS = 'overflow-hidden';
  const SWIPE_THRESHOLD = 48;
  const SWIPE_AXIS_RATIO = 1.15;

  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  class DeebProductMediaModal extends HTMLElement {
    constructor() {
      super();

      this.openedBy = null;
      this.touchStartX = null;
      this.touchStartY = null;
      this.isMovingToBody = false;
      this.hasMovedToBody = false;
      this.listenersBound = false;
      this.destroyed = false;

      this.handleClick = this.handleClick.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      if (this.destroyed) {
        return;
      }

      this.removeDuplicateInstances();
      this.bindListeners();
      this.refresh();

      if (!this.hasMovedToBody && this.parentElement !== document.body) {
        this.hasMovedToBody = true;
        this.isMovingToBody = true;
        document.body.appendChild(this);
        this.isMovingToBody = false;
      }
    }

    disconnectedCallback() {
      if (this.isMovingToBody) {
        return;
      }

      if (this.isOpen) {
        this.hide({ dispatch: false, restoreFocus: false });
      } else {
        this.pauseMedia();
      }

      this.unbindListeners();
    }

    bindListeners() {
      if (this.listenersBound) {
        return;
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('pointerup', this.handlePointerUp);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('touchstart', this.handleTouchStart, {
        passive: true,
      });
      this.addEventListener('touchend', this.handleTouchEnd, {
        passive: true,
      });
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.listenersBound = true;
    }

    unbindListeners() {
      if (!this.listenersBound) {
        return;
      }

      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('pointerup', this.handlePointerUp);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('touchstart', this.handleTouchStart);
      this.removeEventListener('touchend', this.handleTouchEnd);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      this.listenersBound = false;
    }

    removeDuplicateInstances() {
      if (!this.id || typeof window.CSS?.escape !== 'function') {
        return;
      }

      document
        .querySelectorAll(`#${window.CSS.escape(this.id)}`)
        .forEach((candidate) => {
          if (candidate === this) {
            return;
          }

          if (typeof candidate.destroy === 'function') {
            candidate.destroy({ remove: true });
          } else {
            candidate.remove();
          }
        });
    }

    refresh(preferredMediaId = null) {
      this.dialog = this.querySelector('[data-deeb-product-media-dialog]');
      this.stage = this.querySelector('[data-deeb-product-media-content]');
      this.thumbnailStrip = this.querySelector('[data-deeb-product-media-thumbnails]');
      this.liveRegion = this.querySelector('[data-deeb-product-media-live]');

      const requestedMediaId = String(
        preferredMediaId || this.dataset.activeMediaId || ''
      ).trim();
      const fallbackMediaId = this.mediaItems[0]?.dataset.mediaId || '';
      const activeMediaId = this.getMediaItem(requestedMediaId)
        ? requestedMediaId
        : fallbackMediaId;

      if (activeMediaId) {
        this.activateMedia(activeMediaId, {
          announce: false,
          load: this.isOpen,
          scrollThumbnail: false,
        });
      }

      this.thumbnailStrip?.toggleAttribute('hidden', this.mediaItems.length < 2);
    }

    show(opener) {
      if (!this.isConnected || !this.dialog) {
        return;
      }

      this.openedBy = opener instanceof HTMLElement ? opener : null;
      const requestedMediaId = String(
        this.openedBy?.dataset.mediaId || this.dataset.activeMediaId || ''
      ).trim();
      const fallbackMediaId = this.mediaItems[0]?.dataset.mediaId || '';

      this.pausePageMedia();
      this.setAttribute(OPEN_ATTRIBUTE, '');
      this.setAttribute('aria-hidden', 'false');
      this.openedBy?.setAttribute('aria-expanded', 'true');
      document.body.classList.add(BODY_LOCK_CLASS);

      this.activateMedia(requestedMediaId || fallbackMediaId, {
        announce: false,
        load: true,
        scrollThumbnail: true,
      });

      window.requestAnimationFrame(() => {
        if (!this.isOpen) {
          return;
        }

        if (typeof window.trapFocus === 'function') {
          window.trapFocus(this, this.dialog);
        } else {
          this.dialog.focus({ preventScroll: true });
        }

        this.announceActiveMedia();
      });
    }

    hide(options = {}) {
      const {
        dispatch = true,
        restoreFocus = true,
      } = options;

      if (!this.isOpen) {
        return;
      }

      const focusTarget = restoreFocus && this.openedBy?.isConnected
        ? this.openedBy
        : null;

      this.removeAttribute(OPEN_ATTRIBUTE);
      this.setAttribute('aria-hidden', 'true');
      this.openedBy?.removeAttribute('aria-expanded');
      this.resetTouch();
      this.pauseMedia(this);

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(focusTarget);
      } else {
        focusTarget?.focus();
      }

      this.openedBy = null;

      if (!document.querySelector('modal-dialog[open], product-modal[open], deeb-product-media-modal[open]')) {
        document.body.classList.remove(BODY_LOCK_CLASS);
      }

      if (dispatch) {
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
      }
    }

    destroy(options = {}) {
      const { remove = false } = options;

      if (this.destroyed) {
        return;
      }

      this.hide({ dispatch: false, restoreFocus: false });
      this.pauseMedia(this);
      this.unbindListeners();
      this.destroyed = true;

      if (remove && this.isConnected) {
        this.remove();
      }
    }

    replaceMediaContent(sourceModal, preferredMediaId = null) {
      if (!(sourceModal instanceof Element)) {
        return false;
      }

      const sourceStage = sourceModal.querySelector(
        '[data-deeb-product-media-content]'
      );
      const sourceThumbnails = sourceModal.querySelector(
        '[data-deeb-product-media-thumbnails]'
      );

      if (!sourceStage || !this.stage) {
        return false;
      }

      this.pauseMedia(this);
      this.stage.replaceChildren(
        ...Array.from(sourceStage.children, (child) => child.cloneNode(true))
      );

      if (this.thumbnailStrip) {
        this.thumbnailStrip.replaceChildren(
          ...Array.from(
            sourceThumbnails?.children || [],
            (child) => child.cloneNode(true)
          )
        );
      }

      this.refresh(preferredMediaId);
      window.ProductModel?.loadShopifyXR?.();

      return true;
    }

    activateMedia(mediaId, options = {}) {
      const {
        announce = true,
        focusThumbnail = false,
        load = this.isOpen,
        scrollThumbnail = true,
      } = options;
      const normalizedMediaId = String(mediaId || '').trim();
      const activeItem = this.getMediaItem(normalizedMediaId);

      if (!activeItem) {
        return false;
      }

      const previousActiveItem = this.activeMediaItem;

      if (this.isOpen && previousActiveItem) {
        this.pauseMedia(previousActiveItem);
      }

      this.mediaItems.forEach((item) => {
        const isActive = item === activeItem;

        item.classList.toggle(ACTIVE_CLASS, isActive);
        item.setAttribute('aria-hidden', String(!isActive));
        item.toggleAttribute('inert', !isActive);
      });

      this.thumbnails.forEach((thumbnail) => {
        const isCurrent = thumbnail.dataset.deebProductMediaTarget === normalizedMediaId;

        thumbnail.toggleAttribute('aria-current', isCurrent);
        thumbnail.setAttribute('tabindex', isCurrent ? '0' : '-1');

        if (isCurrent && scrollThumbnail) {
          thumbnail.scrollIntoView({
            behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      });

      this.dataset.activeMediaId = normalizedMediaId;

      if (load) {
        this.loadActiveMedia(activeItem);
      }

      if (focusThumbnail) {
        this.getThumbnail(normalizedMediaId)?.focus();
      }

      if (announce && this.isOpen) {
        this.announceActiveMedia(activeItem);
      }

      this.dispatchEvent(
        new CustomEvent('deeb:product-media:change', {
          bubbles: true,
          detail: {
            mediaId: normalizedMediaId,
            mediaIndex: this.mediaItems.indexOf(activeItem),
          },
        })
      );

      return true;
    }

    changeMedia(direction, options = {}) {
      if (this.mediaItems.length < 2) {
        return false;
      }

      const currentIndex = this.mediaItems.findIndex(
        (item) => item.classList.contains(ACTIVE_CLASS)
      );
      const safeIndex = currentIndex < 0 ? 0 : currentIndex;
      const nextIndex = (
        safeIndex + direction + this.mediaItems.length
      ) % this.mediaItems.length;

      return this.activateMedia(
        this.mediaItems[nextIndex].dataset.mediaId,
        options
      );
    }

    loadActiveMedia(activeItem) {
      const deferredMedia = activeItem.querySelector('deferred-media, product-model');

      activeItem.setAttribute('aria-busy', 'true');

      if (
        deferredMedia &&
        !deferredMedia.hasAttribute('loaded') &&
        typeof deferredMedia.loadContent === 'function'
      ) {
        deferredMedia.loadContent(false);
      }

      const playWhenActive = () => {
        if (!activeItem.classList.contains(ACTIVE_CLASS) || !this.isOpen) {
          activeItem.removeAttribute('aria-busy');
          return;
        }

        this.playMedia(activeItem);
        activeItem.removeAttribute('aria-busy');
      };

      activeItem.querySelectorAll('video').forEach((video) => {
        if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
          video.addEventListener('canplay', playWhenActive, { once: true });
        }
      });

      activeItem.querySelectorAll('iframe').forEach((iframe) => {
        iframe.addEventListener('load', playWhenActive, { once: true });
      });

      window.requestAnimationFrame(playWhenActive);
    }

    playMedia(scope) {
      scope.querySelectorAll('video').forEach((video) => {
        const playPromise = video.play();
        playPromise?.catch?.(() => {});
      });

      scope.querySelectorAll('.js-youtube').forEach((iframe) => {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*'
        );
      });

      scope.querySelectorAll('.js-vimeo').forEach((iframe) => {
        iframe.contentWindow?.postMessage({ method: 'play' }, '*');
      });
    }

    pauseMedia(scope = this) {
      scope.querySelectorAll('video').forEach((video) => video.pause());

      scope.querySelectorAll('.js-youtube').forEach((iframe) => {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*'
        );
      });

      scope.querySelectorAll('.js-vimeo').forEach((iframe) => {
        iframe.contentWindow?.postMessage({ method: 'pause' }, '*');
      });

      scope.querySelectorAll('product-model').forEach((model) => {
        model.modelViewerUI?.pause?.();
      });
    }

    pausePageMedia() {
      if (typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
        return;
      }

      this.pauseMedia(document);
    }

    announceActiveMedia(item = this.activeMediaItem) {
      if (!this.liveRegion || !item) {
        return;
      }

      const label = String(item.dataset.mediaLabel || '').trim();
      const index = String(item.dataset.mediaIndex || '').trim();
      const total = String(item.dataset.mediaTotal || this.mediaItems.length).trim();
      const ofLabel = String(this.dataset.ofLabel || 'of').trim();

      this.liveRegion.textContent = [
        label,
        index && total ? `${index} ${ofLabel} ${total}` : '',
      ]
        .filter(Boolean)
        .join(', ');
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const closeButton = event.target.closest('[data-deeb-product-media-close]');
      if (closeButton && this.contains(closeButton)) {
        event.preventDefault();
        this.hide();
        return;
      }

      const thumbnail = event.target.closest('[data-deeb-product-media-target]');
      if (!thumbnail || !this.contains(thumbnail)) {
        return;
      }

      event.preventDefault();
      this.activateMedia(thumbnail.dataset.deebProductMediaTarget, {
        focusThumbnail: false,
      });
    }

    handlePointerUp(event) {
      if (event.target !== this) {
        return;
      }

      event.preventDefault();
      this.hide();
    }

    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.hide();
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      const focusedThumbnail = target?.closest('[data-deeb-product-media-target]');

      if (focusedThumbnail) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.changeMedia(1, { focusThumbnail: true });
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.changeMedia(-1, { focusThumbnail: true });
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.activateMedia(this.mediaItems[0]?.dataset.mediaId, {
            focusThumbnail: true,
          });
        } else if (event.key === 'End') {
          event.preventDefault();
          this.activateMedia(this.mediaItems[this.mediaItems.length - 1]?.dataset.mediaId, {
            focusThumbnail: true,
          });
        }

        return;
      }

      if (
        target?.closest(
          'button, a, input, textarea, select, video, iframe, model-viewer, [contenteditable="true"]'
        )
      ) {
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.changeMedia(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.changeMedia(-1);
      }
    }

    handleTouchStart(event) {
      if (!this.isOpen || event.touches.length !== 1 || this.shouldIgnoreSwipe(event.target)) {
        this.resetTouch();
        return;
      }

      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    }

    handleTouchEnd(event) {
      if (
        this.touchStartX === null ||
        this.touchStartY === null ||
        event.changedTouches.length !== 1
      ) {
        this.resetTouch();
        return;
      }

      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;

      this.resetTouch();

      if (
        Math.abs(deltaX) < SWIPE_THRESHOLD ||
        Math.abs(deltaX) <= Math.abs(deltaY) * SWIPE_AXIS_RATIO
      ) {
        return;
      }

      this.changeMedia(deltaX < 0 ? 1 : -1);
    }

    shouldIgnoreSwipe(target) {
      return target instanceof Element && Boolean(
        target.closest(
          '[data-deeb-product-media-thumbnails], button, a, video, iframe, model-viewer, deferred-media, product-model'
        )
      );
    }

    handleVisibilityChange() {
      if (document.hidden && this.isOpen) {
        this.pauseMedia(this);
      }
    }

    handleSectionUnload(event) {
      const sectionId = String(
        event.detail?.sectionId ||
          event.target?.id?.replace(/^shopify-section-/, '') ||
          ''
      ).trim();

      if (sectionId && sectionId === String(this.dataset.sectionId || '').trim()) {
        this.destroy({ remove: true });
      }
    }

    resetTouch() {
      this.touchStartX = null;
      this.touchStartY = null;
    }

    getMediaItem(mediaId) {
      const normalizedMediaId = String(mediaId || '').trim();

      return this.mediaItems.find(
        (item) => item.dataset.mediaId === normalizedMediaId
      );
    }

    getThumbnail(mediaId) {
      const normalizedMediaId = String(mediaId || '').trim();

      return this.thumbnails.find(
        (thumbnail) => thumbnail.dataset.deebProductMediaTarget === normalizedMediaId
      );
    }

    get mediaItems() {
      return Array.from(
        this.stage?.querySelectorAll(':scope > [data-deeb-product-media-item]') || []
      );
    }

    get thumbnails() {
      return Array.from(
        this.thumbnailStrip?.querySelectorAll('[data-deeb-product-media-target]') || []
      );
    }

    get activeMediaItem() {
      return this.mediaItems.find((item) => item.classList.contains(ACTIVE_CLASS)) || null;
    }

    get isOpen() {
      return this.hasAttribute(OPEN_ATTRIBUTE);
    }

    get prefersReducedMotion() {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    }
  }

  customElements.define(ELEMENT_NAME, DeebProductMediaModal);
})();
